/**
 * <PageGridCard/> — Slot-based grid card.
 *
 * Used by Marketplace `<TemplateCard>` and Workflows page card.
 * Banner + body + footer with consistent geometry; per-card tint via `tint` prop
 * (sets `--card-tint` CSS var on the root).
 * Non-native pointer targets inside a clickable card must opt out of the
 * primary-action overlay with `data-card-interactive="true"`.
 */
import clsx from 'clsx';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useId } from 'react';

import * as styles from './page-grid-card.css';

export type PageGridCardVariant = 'default' | 'miniature';

export interface PageGridCardProps {
  /** Compact Marketplace thumbnail used by embedded pickers. */
  variant?: PageGridCardVariant;
  /** Hex tint applied to banner gradient, glyph border, type pill, focus ring. */
  tint?: string;
  /** Banner content — typically a glyph wrapped in `<PageGridCardGlyph/>`. */
  banner: ReactNode;
  /** Optional badge rendered absolute top-right inside the banner. */
  bannerBadge?: ReactNode;
  /** Title text. Truncated to 2 lines. */
  title: ReactNode;
  /** Optional pill rendered bottom-left over the banner (e.g. type label). */
  typePill?: ReactNode;
  /** Optional badge rendered in the body title row, aligned right. */
  bodyBadge?: ReactNode;
  /** Optional meta line under the title (author, version, etc.). */
  meta?: ReactNode;
  /** Optional 2-line description. */
  description?: ReactNode;
  /** Optional content above the footer (e.g. stats row). */
  bodyExtra?: ReactNode;
  /** Footer left slot — typically a category chip. */
  footerStart?: ReactNode;
  /** Footer right slot — typically a CTA / status. */
  footerEnd?: ReactNode;
  /** Activates the card's dedicated primary-action button. */
  onClick?: () => void;
  ariaLabel?: string;
  className?: string;
  testId?: string;
  /** Stable resource identity for diagnostics and end-to-end contracts. */
  resourceId?: string;
  disabled?: boolean;
  ariaPressed?: boolean;
  ariaBusy?: boolean;
}

export function PageGridCard({
  variant = 'default',
  tint,
  banner,
  bannerBadge,
  title,
  typePill,
  bodyBadge,
  meta,
  description,
  bodyExtra,
  footerStart,
  footerEnd,
  onClick,
  ariaLabel,
  className,
  testId,
  resourceId,
  disabled = false,
  ariaPressed,
  ariaBusy,
}: PageGridCardProps): ReactElement {
  const titleId = useId();
  const tintStyle: CSSProperties = tint
    ? ({ ['--card-tint' as string]: tint } as CSSProperties)
    : {};

  return (
    <article
      className={clsx(
        styles.card,
        variant === 'miniature' && styles.miniatureCard,
        onClick && !disabled && styles.clickableCard,
        disabled && styles.disabledCard,
        className
      )}
      style={tintStyle}
      data-testid={testId}
      data-resource-id={resourceId}
    >
      {onClick ? (
        <button
          type="button"
          className={styles.cardPrimaryAction}
          onClick={onClick}
          disabled={disabled}
          aria-pressed={ariaPressed}
          aria-busy={ariaBusy}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabel ? undefined : titleId}
        />
      ) : null}

      <div
        className={clsx(
          styles.cardBanner,
          variant === 'miniature' && styles.miniatureCardBanner,
          onClick && styles.cardContentLayer
        )}
      >
        {banner}
        {typePill ? (
          <div
            className={clsx(
              styles.cardBannerTypePill,
              variant === 'miniature' && styles.miniatureCardBannerTypePill
            )}
          >
            {typePill}
          </div>
        ) : null}
        {bannerBadge}
      </div>

      <div
        className={clsx(
          styles.cardBody,
          variant === 'miniature' && styles.miniatureCardBody,
          onClick && styles.cardContentLayer
        )}
      >
        <div className={styles.cardTitleRow}>
          <span id={titleId} className={styles.cardTitle}>
            {title}
          </span>
          {bodyBadge}
        </div>
        {meta ? <div className={styles.cardMeta}>{meta}</div> : null}
        {description ? (
          <div className={styles.cardDescription}>{description}</div>
        ) : null}
        {bodyExtra}
      </div>

      {(variant === 'miniature' || footerStart || footerEnd) && (
        <div
          className={clsx(
            styles.cardFooter,
            variant === 'miniature' && styles.miniatureCardFooter,
            onClick && styles.cardContentLayer
          )}
        >
          {footerStart ?? <span />}
          {variant === 'miniature' && typeof footerEnd === 'string' ? (
            <span className={styles.miniatureCardFooterText} title={footerEnd}>
              {footerEnd}
            </span>
          ) : (
            (footerEnd ?? null)
          )}
        </div>
      )}
    </article>
  );
}

// =============================================================================
// Convenience wrappers
// =============================================================================

export function PageGridCardGlyph({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: PageGridCardVariant;
}): ReactElement {
  return (
    <span
      className={clsx(
        styles.cardBannerGlyph,
        variant === 'miniature' && styles.miniatureCardBannerGlyph
      )}
    >
      {children}
    </span>
  );
}

export function PageGridCardTypePill({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return <span className={styles.cardTypePill}>{children}</span>;
}

export function PageGridCardCategoryChip({
  children,
  title,
}: {
  children: ReactNode;
  title?: string;
}): ReactElement {
  return (
    <span className={styles.cardCategoryChip} title={title}>
      {children}
    </span>
  );
}

export function PageGridContainer({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}): ReactElement {
  return (
    <div className={styles.cardGrid} data-testid={testId}>
      {children}
    </div>
  );
}

export function PageGridCardSkeleton({
  tint,
}: {
  tint?: string;
}): ReactElement {
  const tintStyle: CSSProperties = tint
    ? ({ ['--card-tint' as string]: tint } as CSSProperties)
    : {};

  return (
    <div
      className={clsx(styles.card, styles.skeletonCard)}
      style={tintStyle}
      aria-hidden="true"
    >
      <div className={styles.cardBanner}>
        <span className={styles.skeletonGlyph} />
        <div className={styles.cardBannerTypePill}>
          <span className={styles.skeletonTypePill} />
        </div>
      </div>

      <div className={styles.cardBody}>
        <div className={styles.cardTitleRow}>
          <span className={styles.skeletonTitleLine} />
          <span className={styles.skeletonBadge} />
        </div>
        <span className={styles.skeletonMetaLine} />
        <span
          className={styles.skeletonDescriptionLine}
          style={{ width: '92%' }}
        />
        <span
          className={styles.skeletonDescriptionLine}
          style={{ width: '64%' }}
        />
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.skeletonFooterChip} />
        <span className={styles.skeletonFooterAction} />
      </div>
    </div>
  );
}

export function PageGridSkeleton({
  count = 6,
  testId,
  tint,
}: {
  count?: number;
  testId?: string;
  tint?: string;
}): ReactElement {
  return (
    <PageGridContainer testId={testId}>
      {Array.from({ length: count }).map((_, index) => (
        <PageGridCardSkeleton key={index} tint={tint} />
      ))}
    </PageGridContainer>
  );
}
